import "../assets/css/Career.css";
import { useEffect, useRef, useState } from "react";
import career_bg from "../assets/images/Career/career-bg.jpg";

// ─── Data ────────────────────────────────────────────────────

const heroStats = [
  { num: "5000+", label: "Students Trained" },
  { num: "95%", label: "Placement Rate" },
  { num: "45 Days", label: "Avg. Time to Placement" },
  { num: "4.8★", label: "Student Rating" },
];

const processSteps = [
  {
    num: "01",
    icon: "🎓",
    title: "Industry Training",
    desc: "Hands-on, practical training on real equipment with live project experience from day one.",
  },
  {
    num: "02",
    icon: "🔧",
    title: "Live Project Work",
    desc: "Build a real portfolio project during the course — something tangible to show every employer.",
  },
  {
    num: "03",
    icon: "📝",
    title: "Resume & Profile",
    desc: "Professional resume crafted by our career team — tailored to your domain and target roles.",
  },
  {
    num: "04",
    icon: "🎤",
    title: "Mock Interviews",
    desc: "Practice sessions with industry professionals — technical and HR rounds covered thoroughly.",
  },
  {
    num: "05",
    icon: "🚀",
    title: "Job Referrals",
    desc: "Direct referrals to our hiring network — companies that actively seek Skimitar graduates.",
  },
];

const placementServices = [
  {
    icon: "📝",
    bg: "#FCEBEB",
    title: "Resume Building",
    desc: "Our career team builds a professional, ATS-friendly resume tailored to your domain, skills, and target companies. No generic templates — a resume that truly represents you.",
  },
  {
    icon: "💼",
    bg: "#E6F1FB",
    title: "LinkedIn Optimisation",
    desc: "A strong LinkedIn profile attracts recruiters passively. We help you build a compelling profile that highlights your skills, projects, and certifications — making you visible to hirers.",
  },
  {
    icon: "🎤",
    bg: "#E1F5EE",
    title: "Mock Interview Sessions",
    desc: "Structured mock interviews covering technical questions, HR rounds, and situational scenarios — conducted by professionals who know exactly what employers look for.",
  },
  {
    icon: "🔗",
    bg: "#EEEDFE",
    title: "Industry Referrals",
    desc: "We connect strong graduates directly with companies in our network — a direct referral always carries more weight than a cold application. We use our relationships for your benefit.",
  },
  {
    icon: "🧭",
    bg: "#FAEEDA",
    title: "Career Counselling",
    desc: "One-on-one sessions to help you identify the right career path, understand salary expectations, and make informed decisions about your professional journey after training.",
  },
  {
    icon: "📊",
    bg: "#EAF3DE",
    title: "Job Portal Access",
    desc: "Exclusive access to curated job listings and fresher openings sourced specifically for Skimitar graduates — filtered by domain, location, and experience level.",
  },
];

const hiringDomains = [
  {
    icon: "⚙️",
    bg: "#FCEBEB",
    title: "Electrical & Automation",
    roles: [
      { label: "PLC Engineer", bg: "#FCEBEB", color: "#A32D2D" },
      { label: "SCADA Operator", bg: "#FCEBEB", color: "#A32D2D" },
      { label: "Automation Technician", bg: "#FCEBEB", color: "#A32D2D" },
      { label: "Panel Wiring Engineer", bg: "#FCEBEB", color: "#A32D2D" },
      { label: "Maintenance Engineer", bg: "#FCEBEB", color: "#A32D2D" },
    ],
  },
  {
    icon: "🔌",
    bg: "#E6F1FB",
    title: "Embedded Systems & IoT",
    roles: [
      { label: "Embedded Engineer", bg: "#E6F1FB", color: "#0C447C" },
      { label: "Firmware Developer", bg: "#E6F1FB", color: "#0C447C" },
      { label: "IoT Developer", bg: "#E6F1FB", color: "#0C447C" },
      { label: "PCB Designer", bg: "#E6F1FB", color: "#0C447C" },
      { label: "Hardware Engineer", bg: "#E6F1FB", color: "#0C447C" },
    ],
  },
  {
    icon: "💻",
    bg: "#EEEDFE",
    title: "IT & Software",
    roles: [
      { label: "Web Developer", bg: "#EEEDFE", color: "#3C3489" },
      { label: "Python Developer", bg: "#EEEDFE", color: "#3C3489" },
      { label: "Data Analyst", bg: "#EEEDFE", color: "#3C3489" },
      { label: "AI/ML Engineer", bg: "#EEEDFE", color: "#3C3489" },
      { label: "Cloud Engineer", bg: "#EEEDFE", color: "#3C3489" },
    ],
  },
  {
    icon: "📱",
    bg: "#E1F5EE",
    title: "Digital Marketing",
    roles: [
      { label: "SEO Specialist", bg: "#E1F5EE", color: "#085041" },
      { label: "Social Media Manager", bg: "#E1F5EE", color: "#085041" },
      { label: "PPC Executive", bg: "#E1F5EE", color: "#085041" },
      { label: "Content Marketer", bg: "#E1F5EE", color: "#085041" },
      { label: "Digital Marketing Executive", bg: "#E1F5EE", color: "#085041" },
    ],
  },
];

const successStories = [
  {
    initials: "KR",
    avatarBg: "#cc0000",
    name: "Karthik R.",
    course: "PLC & Automation · B.E. Graduate",
    quote:
      '"The hands-on training gave me real confidence. I walked into my interview knowing exactly what to say and show. Placed within 45 days of completing the course."',
    result: "Now working as Automation Engineer",
    resultBg: "#E1F5EE",
    resultColor: "#085041",
  },
  {
    initials: "PM",
    avatarBg: "#378ADD",
    name: "Priya M.",
    course: "Digital Marketing · Career Switcher",
    quote:
      '"I was working a completely unrelated job. After the digital marketing course, I started freelancing within 3 months and now manage campaigns for 5 clients full-time."',
    result: "Full-time Freelance Digital Marketer",
    resultBg: "#E1F5EE",
    resultColor: "#085041",
  },
  {
    initials: "AK",
    avatarBg: "#1D9E75",
    name: "Arun K.",
    course: "Embedded Systems & IoT · Fresher",
    quote:
      '"The live project was the game-changer. I had a real IoT system in my portfolio before I even started applying. Every interviewer was impressed by that alone."',
    result: "Joined an Electronics Startup",
    resultBg: "#E1F5EE",
    resultColor: "#085041",
  },
];

const enrollCards = [
  {
    icon: "🎓",
    title: "For Students & Freshers",
    desc: "Build job-ready skills with certifications, internships & live projects — with full placement support included.",
  },
  {
    icon: "💼",
    title: "For Working Professionals",
    desc: "Upskill on weekends, get certified, and unlock better roles and higher salaries in your current field.",
  },
  {
    icon: "🏢",
    title: "For Corporate HR Teams",
    desc: "Train your team with customized programs — on-site or online — with assessments and certification.",
  },
  {
    icon: "🚀",
    title: "For Career Switchers",
    desc: "Change your domain completely with structured programs that take you from zero to industry-ready.",
  },
];

// ─── IMPROVED SCROLLING ANIMATION HOOK ────────────────────────
function useScrollReveal(
  sectionRef,
  cardSelector = ".scroll-reveal",
  delay = 80,
) {
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const cards = Array.from(section.querySelectorAll(cardSelector));
    if (!cards.length) return;

    cards.forEach((card, index) => {
      card.style.opacity = "0";
      card.style.transform =
        index % 2 === 0 ? "translateX(-60px)" : "translateX(60px)";
      card.style.transition = "all 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const visibleCards = Array.from(
              entry.target.querySelectorAll(cardSelector),
            );

            visibleCards.forEach((card, idx) => {
              setTimeout(() => {
                card.style.opacity = "1";
                card.style.transform = "translateX(0)";
              }, idx * delay);
            });

            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -50px 0px" },
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, [delay]);
}

// ─── FADE-IN-UP HOOK (unchanged) ──────────────────────────────────────────
function useFadeInUp(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(28px)";
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.transition =
            "opacity 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
          observer.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
}

// ═══════════════════════════════════════════════════════════════
// HERO
// ═══════════════════════════════════════════════════════════════
function Hero() {
  return (
    <section className="cr-hero-section position-relative overflow-hidden">
      <div
        className="cr-hero-bg position-absolute w-100 h-100"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.62), rgba(0, 0, 0, 0.72)), url(${career_bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      ></div>

      <div
        className="container cr-container position-relative"
        style={{ zIndex: 1 }}
      >
        <div className="d-flex align-items-center justify-content-center gap-3 mb-3 cr-fade-in">
          <div className="cr-eyebrow-line"></div>
          <span className="cr-eyebrow-text text-uppercase fw-semibold">
            Career &amp; Placement
          </span>
          <div className="cr-eyebrow-line"></div>
        </div>

        <h1
          className="cr-hero-title text-white text-center fw-bold mb-3 cr-fade-in"
          style={{ animationDelay: "0.1s" }}
        >
          We Don't Just Train You.
          <br />
          <em className="text-primary-red" style={{ fontStyle: "normal" }}>
            We Launch Your Career.
          </em>
        </h1>

        <p
          className="cr-hero-sub text-center mx-auto mb-4 cr-fade-in"
          style={{ animationDelay: "0.2s" }}
        >
          At Skimitar Hindusthan, placement support is not an afterthought — it
          is built into every program we offer. From day one, we prepare you for
          the industry, not just the exam.
        </p>

        <div
          className="row g-2 justify-content-center cr-fade-in"
          style={{ animationDelay: "0.3s" }}
        >
          {heroStats.map((s) => (
            <div key={s.num} className="col-6 col-sm-3">
              <div className="cr-stat-box text-center border border-white border-opacity-25 rounded-4 py-3 px-2 bg-transparent card h-100">
                <span className="cr-stat-num text-danger d-block fw-bolder">
                  {s.num}
                </span>
                <span className="cr-stat-lbl text-white">{s.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// HONEST PROMISE
// ═══════════════════════════════════════════════════════════════
function HonestPromise() {
  const ref = useRef(null);
  useFadeInUp(ref);

  return (
    <section className="py-5">
      <div className="container cr-container" ref={ref}>
        <div className="cr-honest-box border-start border-4 border-primary-red bg-light-subtle p-4 rounded-2">
          <p
            className="fw-bold text-uppercase mb-2"
            style={{ fontSize: "15px", letterSpacing: "0.05em" }}
          >
            Our Honest Placement Promise
          </p>
          <p className="mb-0" style={{ fontSize: "15px", lineHeight: "1.8" }}>
            We will never guarantee 100% placement — no honest training
            institute can. What we do promise is this: every student who
            completes our program with dedication will receive our full career
            support — resume, interviews, referrals, and guidance — until they
            land the right opportunity. Your success is our reputation.
          </p>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// PROCESS STEPS
// ═══════════════════════════════════════════════════════════════
function ProcessSection() {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef, ".process-seq-card", 90);

  return (
    <section className="py-5 bg_color cr-margin-top" ref={sectionRef}>
      <div className="container cr-container py-lg-4">
        <div className="mb-5">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="cr-sec-line"></div>
            <span className="cr-sec-label text-uppercase fw-semibold text-primary-red">
              Our Process
            </span>
            <div className="cr-sec-line"></div>
          </div>
          <h2 className="cr-section-title fw-bold text-uppercase mt-2">
            How We Take You From Training to Employment
          </h2>
          <p className="cr-section-sub mt-2">
            A structured, step-by-step journey — from the first day of your
            course to your first day at work.
          </p>
        </div>

        <div className="row g-4">
          {processSteps.map((step, index) => (
            <div
              key={step.num}
              className="col-12 col-sm-6 col-lg process-seq-card scroll-reveal"
            >
              <div className="cr-process-card card h-100 border-0 shadow-sm rounded-0">
                <div className="card-body p-4 text-center">
                  <div className="cr-ps-num rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3">
                    {step.num}
                  </div>
                  <div className="cr-ps-icon mb-2">{step.icon}</div>
                  <p
                    className="fw-bold text-uppercase mb-2"
                    style={{ fontSize: "15px" }}
                  >
                    {step.title}
                  </p>
                  <p
                    className="mb-0"
                    style={{
                      fontSize: "15px",
                      color: "#555",
                      lineHeight: "1.7",
                    }}
                  >
                    {step.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// PLACEMENT SERVICES
// ═══════════════════════════════════════════════════════════════
function PlacementServices() {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef, ".pl-seq-card", 85);

  return (
    <section className="py-5 cr-margin-top" ref={sectionRef}>
      <div className="container cr-container py-lg-4">
        <div className="mb-5">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="cr-sec-line"></div>
            <span className="cr-sec-label text-uppercase fw-semibold text-primary-red">
              Placement Support
            </span>
            <div className="cr-sec-line"></div>
          </div>
          <h2 className="cr-section-title fw-bold text-uppercase mt-2">
            Everything We Do to Get You Placed
          </h2>
        </div>

        <div className="row g-4">
          {placementServices.map((s, index) => (
            <div
              key={s.title}
              className="col-md-6 col-lg-4 pl-seq-card scroll-reveal"
            >
              <div className="cr-pl-card card h-100 border-0 shadow-sm rounded-0 bg-white">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div
                      className="cr-pl-icon d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                      style={{
                        background: s.bg,
                        width: "44px",
                        height: "44px",
                        fontSize: "20px",
                      }}
                    >
                      {s.icon}
                    </div>
                    <p
                      className="fw-bold text-uppercase mb-0"
                      style={{ fontSize: "15px" }}
                    >
                      {s.title}
                    </p>
                  </div>
                  <p
                    className="mb-0"
                    style={{
                      fontSize: "15px",
                      color: "#555",
                      lineHeight: "1.75",
                    }}
                  >
                    {s.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// HIRING DOMAINS
// ═══════════════════════════════════════════════════════════════
function HiringDomains() {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef, ".dom-seq-card", 90);

  return (
    <section className="py-5 bg_color cr-margin-top" ref={sectionRef}>
      <div className="container cr-container py-lg-4">
        <div className="mb-5">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="cr-sec-line"></div>
            <span className="cr-sec-label text-uppercase fw-semibold text-primary-red">
              Career Paths
            </span>
            <div className="cr-sec-line"></div>
          </div>
          <h2 className="cr-section-title fw-bold text-uppercase mt-2">
            Roles Our Graduates Are Placed In
          </h2>
          <p className="cr-section-sub mt-2">
            Here are the most common job roles and career paths that Skimitar
            graduates pursue after completing their programs.
          </p>
        </div>

        <div className="row g-4">
          {hiringDomains.map((d, index) => (
            <div key={d.title} className="col-md-6 dom-seq-card scroll-reveal">
              <div className="cr-dom-card card h-100 border-0 shadow-sm rounded-0 bg-white">
                <div className="card-body p-4 d-flex gap-3">
                  <div
                    className="cr-dom-icon d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                    style={{
                      background: d.bg,
                      width: "48px",
                      height: "48px",
                      fontSize: "22px",
                    }}
                  >
                    {d.icon}
                  </div>
                  <div className="flex-grow-1">
                    <p
                      className="fw-bold text-uppercase mb-3"
                      style={{ fontSize: "15px" }}
                    >
                      {d.title}
                    </p>
                    <div className="d-flex flex-wrap gap-2">
                      {d.roles.map((r) => (
                        <span
                          key={r.label}
                          className="cr-role-tag rounded-pill px-3 py-1"
                          style={{
                            background: r.bg,
                            color: r.color,
                            fontSize: "15px",
                            fontWeight: "500",
                          }}
                        >
                          {r.label}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// SUCCESS STORIES
// ═══════════════════════════════════════════════════════════════
function SuccessStories() {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef, ".story-seq-card", 100);

  return (
    <section className="py-5 cr-margin-top" ref={sectionRef}>
      <div className="container cr-container py-lg-4">
        <div className="mb-5">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="cr-sec-line"></div>
            <span className="cr-sec-label text-uppercase fw-semibold text-primary-red">
              Success Stories
            </span>
            <div className="cr-sec-line"></div>
          </div>
          <h2 className="cr-section-title fw-bold text-uppercase mt-2">
            Real Students. Real Careers. Real Impact.
          </h2>
        </div>

        <div className="row g-4">
          {successStories.map((s, index) => (
            <div key={s.name} className="col-md-4 story-seq-card scroll-reveal">
              <div className="cr-story-card card h-100 border-0 shadow-sm rounded-0 overflow-hidden">
                <div className="cr-story-top p-4">
                  <div
                    className="cr-story-avatar d-flex align-items-center justify-content-center rounded-circle mb-3 fw-bold"
                    style={{
                      background: s.avatarBg,
                      color: "#fff",
                      width: "48px",
                      height: "48px",
                      fontSize: "15px",
                    }}
                  >
                    {s.initials}
                  </div>
                  <p
                    className="fw-bold text-white mb-1"
                    style={{ fontSize: "15px" }}
                  >
                    {s.name}
                  </p>
                  <p
                    className="mb-0"
                    style={{ fontSize: "15px", color: "#aaa" }}
                  >
                    {s.course}
                  </p>
                </div>
                <div className="card-body p-4 bg-white">
                  <p
                    className="fst-italic mb-3"
                    style={{
                      fontSize: "15px",
                      color: "#555",
                      lineHeight: "1.75",
                    }}
                  >
                    {s.quote}
                  </p>
                  <div
                    className="d-flex align-items-center gap-2 rounded-2 px-3 py-2"
                    style={{ background: s.resultBg }}
                  >
                    <span className="cr-story-dot flex-shrink-0"></span>
                    <span
                      className="fw-semibold"
                      style={{ fontSize: "15px", color: s.resultColor }}
                    >
                      {s.result}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// JOB OPENINGS
// ═══════════════════════════════════════════════════════════════
function JobOpenings() {
  const ref = useRef(null);
  useFadeInUp(ref);

  const jobs = [
    {
      designation: "PLC Automation Engineer",
      company: "Skimitar Hindusthan Pvt Ltd",
      description:
        "Experience with Siemens / Allen-Bradley PLCs, SCADA configuration, and panel wiring. Minimum 1 year experience preferred.",
      location: "Erode, Tamil Nadu",
      vacancies: "2 Openings",
      deadline: "30 Apr 2025",
    },
    {
      designation: "Embedded Systems Trainee",
      company: "Skimitar Hindusthan Pvt Ltd",
      description:
        "Freshers with knowledge of ARM Cortex, Arduino, Raspberry Pi and basic C/C++ programming. Live project experience is a plus.",
      location: "Erode, Tamil Nadu",
      vacancies: "3 Openings",
      deadline: "15 May 2025",
    },
    {
      designation: "Digital Marketing Executive",
      company: "Skimitar Hindusthan Pvt Ltd",
      description:
        "Proficiency in SEO, Google Ads, Meta Ads, and content strategy. Candidates with our certification will be preferred.",
      location: "Erode / Remote",
      vacancies: "1 Opening",
      deadline: "20 Apr 2025",
    },
  ];

  return (
    <section className="py-5 bg_color cr-margin-top">
      <div className="container cr-container py-lg-4" ref={ref}>
        <div className="mb-5">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="cr-sec-line"></div>
            <span className="cr-sec-label text-uppercase fw-semibold text-primary-red">
              Open Positions
            </span>
            <div className="cr-sec-line"></div>
          </div>
          <h2 className="cr-section-title fw-bold text-uppercase mt-2">
            Current Job Openings
          </h2>
          <p className="cr-section-sub mt-2">
            Explore open roles at Skimitar Hindusthan and our partner companies.
            Apply directly or reach out to our placement team.
          </p>
        </div>

        <div className="d-flex flex-column gap-4">
          {jobs.map((job, idx) => (
            <div
              key={idx}
              className="cr-job-card card border-0 shadow-sm rounded-0 scroll-reveal"
            >
              <div className="career_bg px-4 py-3 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                <div>
                  <p
                    className="fw-bold text-uppercase mb-1"
                    style={{ fontSize: "20px" }}
                  >
                    {job.designation}
                  </p>
                  <div className="d-flex align-items-center gap-2">
                    <i className="fa-solid fa-building text-primary-red"></i>
                    <span style={{ fontSize: "15px" }}>{job.company}</span>
                  </div>
                </div>
                <button className="btn btn-danger cr-apply-btn fw-bold text-uppercase align-self-start align-self-md-center">
                  Apply Now
                </button>
              </div>
              <div className="card-body px-4 py-3 bg-white">
                <p
                  className="mb-3"
                  style={{
                    fontSize: "15px",
                    color: "#555",
                    lineHeight: "1.75",
                  }}
                >
                  <i className="fa-solid fa-list text-primary-red me-2"></i>
                  {job.description}
                </p>
                <div className="row g-2">
                  <div className="col-12 col-sm-4">
                    <span style={{ fontSize: "15px" }}>
                      <i className="fa-solid fa-location-pin text-primary-red me-2"></i>
                      <strong>{job.location}</strong>
                    </span>
                  </div>
                  <div className="col-12 col-sm-4">
                    <span style={{ fontSize: "15px" }}>
                      <i className="fa-solid fa-users text-primary-red me-2"></i>
                      <strong>{job.vacancies}</strong>
                    </span>
                  </div>
                  <div className="col-12 col-sm-4">
                    <span style={{ fontSize: "15px" }}>
                      <i className="fa-solid fa-calendar-days text-primary-red me-2"></i>
                      Deadline: <strong>{job.deadline}</strong>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// ENROLL / COUNSELLING FORM
// ═══════════════════════════════════════════════════════════════
function EnrollSection() {
  const [isChecked, setIsChecked] = useState(false);
  const ref = useRef(null);
  useFadeInUp(ref);

  return (
    <section className="py-5 cr-margin-top">
      <div className="container cr-container py-lg-4" ref={ref}>
        <div className="mb-5">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="cr-sec-line"></div>
            <span className="cr-sec-label text-uppercase fw-semibold text-primary-red">
              Get Started
            </span>
            <div className="cr-sec-line"></div>
          </div>
          <h2 className="cr-section-title fw-bold text-uppercase mt-2">
            Ready to Start Your Career Journey?
          </h2>
          <p className="cr-section-sub mt-2">
            Tell us your background and what you're looking for — our
            counsellors will guide you to the right program and placement path.
          </p>
        </div>

        <div className="row g-4 align-items-start">
          <div className="col-lg-6">
            <div className="row g-3">
              {enrollCards.map((c, index) => (
                <div key={c.title} className="col-12 col-sm-6 scroll-reveal">
                  <div className="cr-enroll-card card h-100 border-0 shadow-sm rounded-0">
                    <div className="card-body p-3 d-flex gap-3">
                      <span style={{ fontSize: "26px", flexShrink: 0 }}>
                        {c.icon}
                      </span>
                      <div>
                        <p
                          className="fw-bold mb-1"
                          style={{ fontSize: "15px" }}
                        >
                          {c.title}
                        </p>
                        <p
                          className="mb-0"
                          style={{
                            fontSize: "15px",
                            color: "#555",
                            lineHeight: "1.7",
                          }}
                        >
                          {c.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-lg-6">
            <div className="cr-form-card card border-0 shadow rounded-0 scroll-reveal">
              <div className="card-body p-4 p-lg-5">
                <p
                  className="fw-bold border-bottom pb-3 mb-4"
                  style={{ fontSize: "20px" }}
                >
                  📋 Talk to Our Career Counsellor
                </p>

                <div className="mb-3">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: "15px" }}
                  >
                    Your Name *
                  </label>
                  <input
                    type="text"
                    className="form-control cr-input"
                    placeholder="Full name"
                  />
                </div>
                <div className="mb-3">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: "15px" }}
                  >
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    className="form-control cr-input"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
                <div className="mb-3">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: "15px" }}
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="form-control cr-input"
                    placeholder="you@email.com"
                  />
                </div>
                <div className="mb-3">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: "15px" }}
                  >
                    Current Status
                  </label>
                  <select className="form-select cr-input">
                    <option>Select your status</option>
                    <option>Student / Fresher</option>
                    <option>Working Professional</option>
                    <option>Career Switcher</option>
                    <option>Business Owner</option>
                    <option>HR / Corporate</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: "15px" }}
                  >
                    Domain of Interest
                  </label>
                  <select className="form-select cr-input">
                    <option>Select a domain</option>
                    <option>Electrical &amp; Automation</option>
                    <option>Embedded Systems &amp; IoT</option>
                    <option>IT &amp; Software Development</option>
                    <option>AI &amp; Data Science</option>
                    <option>Digital Marketing</option>
                    <option>Not Sure — Need Guidance</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: "15px" }}
                  >
                    Upload Resume (Optional)
                  </label>
                  <input type="file" className="form-control cr-input" />
                </div>

                <div className="d-flex gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="cr-terms"
                    className="form-check-input mt-1 flex-shrink-0"
                    checked={isChecked}
                    onChange={() => setIsChecked(!isChecked)}
                    style={{
                      width: "18px",
                      height: "18px",
                      cursor: "pointer",
                      accentColor: "#cc0000",
                    }}
                  />
                  <label
                    htmlFor="cr-terms"
                    style={{
                      fontSize: "15px",
                      color: "#555",
                      lineHeight: "1.65",
                      cursor: "pointer",
                    }}
                  >
                    By joining our community, we will store your personal
                    information and consider you for job opportunities and
                    programs aligned with your interests.
                  </label>
                </div>

                <button
                  type="button"
                  className="btn w-100 cr-submit-btn fw-bold text-uppercase py-3"
                  disabled={!isChecked}
                  style={{ fontSize: "15px", opacity: isChecked ? 1 : 0.5 }}
                >
                  Book Free Counselling Session →
                </button>
                <p
                  className="text-center mt-3 mb-0"
                  style={{ fontSize: "15px", color: "#aaa" }}
                >
                  Free · No commitment · Response within 24 hours
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// CTA
// ═══════════════════════════════════════════════════════════════
function CTA() {
  return (
    <section className="py-5 cr-margin-top">
      <div
        className="cr-cta-wrap p-4 p-lg-5"
        style={{
          background: "linear-gradient(135deg, #cc0000 0%, #930000 100%)",
        }}
      >
        <div className="d-flex align-items-center justify-content-center gap-3 mb-3">
          <div
            className="cr-eyebrow-line"
            style={{ background: "rgba(255,255,255,0.4)" }}
          ></div>
          <span
            className="cr-eyebrow-text text-uppercase fw-semibold text-white"
            style={{ opacity: 0.8 }}
          >
            Your Career Starts Here
          </span>
          <div
            className="cr-eyebrow-line"
            style={{ background: "rgba(255,255,255,0.4)" }}
          ></div>
        </div>
        <div className="row g-4 align-items-center">
          <div className="col-lg-7 text-center text-lg-start">
            <h2
              className="fw-bolder text-white text-uppercase mb-3"
              style={{ fontSize: "25px" }}
            >
              Your Next Career Move Begins at Skimitar
            </h2>
            <p
              className="text-white mb-0"
              style={{ fontSize: "15px", opacity: 0.85, lineHeight: "1.75" }}
            >
              Join hundreds of students who chose the right training, built real
              skills, and landed the careers they wanted — with Skimitar's
              honest, practical support every step of the way.
            </p>
          </div>
          <div className="col-lg-5">
            <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-end">
              <button
                className="btn cr-btn-white px-4 py-3 fw-bold text-uppercase"
                style={{ fontSize: "15px" }}
              >
                Explore Courses →
              </button>
              <button
                className="btn cr-btn-ghost px-4 py-3 fw-bold text-uppercase"
                style={{ fontSize: "15px" }}
              >
                📞 (+91) 8428010909
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── MAIN EXPORT ──────────────────────────────────────────────
export default function Career() {
  return (
    <div
      className="career-page"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <Hero />
      <HonestPromise />
      <ProcessSection />
      <PlacementServices />
      <HiringDomains />
      <SuccessStories />
      <JobOpenings />
      <EnrollSection />
      <CTA />
    </div>
  );
}
